import Box from "@mui/material/Box";
import { styled, Theme } from "@mui/material/styles";
import { MDBoxOwnerState } from "./types";

interface StyledBoxProps {
  theme: Theme;
  ownerState: MDBoxOwnerState;
}

export default styled(Box)<{ ownerState: MDBoxOwnerState }>(({ theme, ownerState }: StyledBoxProps) => {
  const { palette } = theme;
  const { variant, bgColor, color, opacity, borderRadius, shadow, coloredShadow } = ownerState;

  const greyColors: Record<string, string> = {
    "grey-100": palette.grey[100],
    "grey-200": palette.grey[200],
    "grey-300": palette.grey[300],
    "grey-400": palette.grey[400],
    "grey-500": palette.grey[500],
    "grey-600": palette.grey[600],
    "grey-700": palette.grey[700],
    "grey-800": palette.grey[800],
    "grey-900": palette.grey[900],
  };

  const validGradients = [
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
  ];

  const validColors = [
    "transparent",
    "white",
    "black",
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
    "text",
    "grey-100",
    "grey-200",
    "grey-300",
    "grey-400",
    "grey-500",
    "grey-600",
    "grey-700",
    "grey-800",
    "grey-900",
  ];

  const validBorderRadius = ["xs", "sm", "md", "lg", "xl", "xxl", "section"];
  const validBoxShadows = ["xs", "sm", "md", "lg", "xl", "xxl", "inset"];

  // background value
  let backgroundValue: string = bgColor;

  if (variant === "gradient") {
    // For gradient variant, use a simple linear gradient as fallback
    if (validGradients.includes(bgColor)) {
      const paletteColor = palette[bgColor as keyof typeof palette] as any;
      if (paletteColor && typeof paletteColor === 'object' && paletteColor.main) {
        backgroundValue = `linear-gradient(195deg, ${paletteColor.main}, ${paletteColor.dark || paletteColor.main})`;
      } else {
        backgroundValue = `linear-gradient(195deg, ${palette.primary.main}, ${palette.primary.dark})`;
      }
    } else {
      backgroundValue = palette.background.paper;
    }
  } else if (validColors.includes(bgColor)) {
    if (bgColor === 'transparent') {
      backgroundValue = 'transparent';
    } else if (bgColor === 'white') {
      backgroundValue = palette.common.white;
    } else if (bgColor === 'black') {
      backgroundValue = palette.common.black;
    } else if (greyColors[bgColor]) {
      backgroundValue = greyColors[bgColor];
    } else {
      const paletteColor = palette[bgColor as keyof typeof palette] as any;
      if (paletteColor && typeof paletteColor === 'object' && paletteColor.main) {
        backgroundValue = paletteColor.main;
      } else {
        backgroundValue = bgColor;
      }
    }
  }

  // color value
  let colorValue: string = color;
  if (validColors.includes(color)) {
    if (color === 'transparent') {
      colorValue = 'transparent';
    } else if (color === 'white') {
      colorValue = palette.common.white;
    } else if (color === 'black') {
      colorValue = palette.common.black;
    } else if (greyColors[color]) {
      colorValue = greyColors[color];
    } else {
      const paletteColor = palette[color as keyof typeof palette] as any;
      if (paletteColor && typeof paletteColor === 'object' && paletteColor.main) {
        colorValue = paletteColor.main;
      } else {
        colorValue = color;
      }
    }
  }

  // borderRadius value
  let borderRadiusValue: string | number = borderRadius;
  if (validBorderRadius.includes(borderRadius)) {
    // Map to theme spacing or use default values
    const radiusMap: Record<string, number> = {
      xs: 2,
      sm: 4,
      md: 8,
      lg: 12,
      xl: 16,
      xxl: 24,
      section: 32,
    };
    borderRadiusValue = radiusMap[borderRadius] || borderRadius;
  }

  // boxShadow value
  let boxShadowValue: string = "none";
  if (validBoxShadows.includes(shadow)) {
    // For now, use MUI's default shadows
    const shadowMap: Record<string, number> = {
      xs: 1,
      sm: 2,
      md: 4,
      lg: 8,
      xl: 12,
      xxl: 16,
      inset: 0,
    };
    const shadowIndex = shadowMap[shadow] || 0;
    boxShadowValue = theme.shadows[shadowIndex] || "none";
  } else if (coloredShadow && coloredShadow !== "none") {
    // Simple colored shadow implementation
    const shadowColor = palette[coloredShadow as keyof typeof palette] as any;
    if (shadowColor && typeof shadowColor === 'object' && shadowColor.main) {
      boxShadowValue = `0 4px 20px 0 ${shadowColor.main}40`;
    } else {
      boxShadowValue = "none";
    }
  }

  return {
    opacity,
    background: backgroundValue,
    color: colorValue,
    borderRadius: borderRadiusValue,
    boxShadow: boxShadowValue,
  };
});